import type { IAuthContext } from "@/application/ports/IAuthContext";
import type { User } from "@/domain";
import type { AuthState } from "@/domain/types";
import { getErrorMessage } from "@/domain/errors";
import { useToast } from "@/hooks/useToast";
import { identifyUser, resetAnalytics } from "@/infrastructure/analytics";
import {
	getLoginUseCase,
	getLoginWithTokensUseCase,
	getLogoutUseCase,
	getTokenManager,
	getUserStorageService,
	getValidateAuthUseCase,
	getSessionExpiredNotifier,
	getLoginRedirectNotifier,
} from "@/infrastructure/di";
import { queryKeys } from "@/application/queries";
import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	useCallback,
	useEffect,
	useState,
	type ReactNode,
} from "react";

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
	const queryClient = useQueryClient();
	const [authState, setAuthState] = useState<AuthState>({
		user: null,
		isAuthenticated: false,
		isLoading: true,
		error: null,
	});

	const { showToast } = useToast();

	const checkAuthStatus = useCallback(async () => {
		setAuthState((prev) => ({ ...prev, isLoading: true }));

		try {
			const result = await getValidateAuthUseCase().execute();
			setAuthState(result);

			if (result.isAuthenticated && result.user) {
				identifyUser(result.user);
				queryClient.invalidateQueries({
					queryKey: queryKeys.organization.userOrganizations(),
				});
			}
		} catch (error) {
			setAuthState({
				user: null,
				isAuthenticated: false,
				isLoading: false,
				error: getErrorMessage(error, "Authentication check failed"),
			});
		}
	}, [queryClient]);

	const login = useCallback(
		async (email: string, password: string) => {
			setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

			try {
				const result = await getLoginUseCase().execute({ email, password });

				if (!result.success) {
					setAuthState((prev) => ({
						...prev,
						isLoading: false,
						error: result.error || "Login failed",
					}));
					throw new Error(result.error || "Login failed");
				}

				setAuthState({
					user: result.user!,
					isAuthenticated: true,
					isLoading: false,
					error: null,
				});

				identifyUser(result.user!);

				// Reset session expiry guard so future 401s are handled correctly
				const { apiClient } = await import("@/infrastructure/api/client");
				apiClient.resetSessionState();

				queryClient.invalidateQueries({
					queryKey: queryKeys.organization.userOrganizations(),
				});
			} catch (error) {
				setAuthState((prev) => ({
					...prev,
					isLoading: false,
					error: getErrorMessage(error, "Login failed"),
				}));
				throw error;
			}
		},
		[queryClient],
	);

	const loginWithTokens = useCallback(
		async (accessToken: string, refreshToken: string, user: User) => {
			setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

			const result = getLoginWithTokensUseCase().execute(
				accessToken,
				refreshToken,
				user,
			);
			if (!result.success) {
				const errorMsg = result.error || "Token login failed";
				setAuthState((prev) => ({
					...prev,
					isLoading: false,
					error: errorMsg,
				}));
				throw new Error(errorMsg);
			}

			setAuthState({
				user,
				isAuthenticated: true,
				isLoading: false,
				error: null,
			});

			identifyUser(user);
		},
		[],
	);

	const logout = useCallback(async () => {
		setAuthState((prev) => ({ ...prev, isLoading: true }));

		try {
			await getLogoutUseCase().execute();
		} catch {
			// LogoutUseCase already clears storage and Sentry context in its finally block
		} finally {
			resetAnalytics();
			setAuthState({
				user: null,
				isAuthenticated: false,
				isLoading: false,
				error: null,
			});
		}
	}, []);

	const updateUser = useCallback((updatedUser: Partial<User>) => {
		let mergedUser: User | null = null;
		setAuthState((prev) => {
			if (!prev.user) return prev;
			mergedUser = { ...prev.user, ...updatedUser };
			return { ...prev, user: mergedUser };
		});
		if (mergedUser) {
			getUserStorageService().setCurrentUser(mergedUser);
		}
	}, []);

	// Handle session expiration events dispatched by the API client interceptor
	useEffect(() => {
		return getSessionExpiredNotifier().subscribe((message?: string) => {
			getLogoutUseCase()
				.execute()
				.catch(() => {
					// Ignore errors — we are already handling the expired session state
				})
				.finally(() => {
					resetAnalytics();
					setAuthState({
						user: null,
						isAuthenticated: false,
						isLoading: false,
						error: null,
					});

					showToast(
						message || "Your session has expired. Please log in again.",
						"error",
						5000,
					);
					getLoginRedirectNotifier().notify();
				});
		});
	}, [showToast]);

	useEffect(() => {
		checkAuthStatus();
	}, [checkAuthStatus]);

	const value: IAuthContext = {
		user: authState.user,
		isAuthenticated: authState.isAuthenticated,
		isLoading: authState.isLoading,
		error: authState.error,
		login,
		loginWithTokens,
		logout,
		checkAuthStatus,
		updateUser,
		tokenManagement: getTokenManager(),
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
